import { Field, FieldProps, getIn, useFormikContext } from 'formik'
import { FieldConfig as FormikFieldConfig } from 'formik/dist/Field'
import React, { useEffect, type JSX } from 'react'

import { RadioGroup } from '@shared/components/RadioGroup/RadioGroup'

import { DeepNonNullable } from '../types/DeepNonNullable'
import { DeepRequired } from '../types/DeepRequired'
import { TypedFieldProps } from '../types/TypedFieldProps'
import { ValidateField } from '../types/ValidateField'
import { getFieldValid } from './helpers'

interface RadioGroupProps extends Omit<FormikFieldConfig, 'name'> {
  onChange?(value: string | number): void
  wrapped?: boolean
  horizontal?: boolean
  children: React.ReactNode
}

export function RawRadioGroupField<FormValues>(
  props: RadioGroupProps & TypedFieldProps<FormValues>
): JSX.Element {
  const { namePrefix = '', name = '', value, onChange, children, ...inputProps } = props

  const context = useFormikContext<FormValues>()

  const error = getIn(context.errors, name)
  const touched = getIn(context.touched, name)
  const isValid = getFieldValid(error, touched)

  useEffect(() => {
    context.setFieldValue(name, value)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleRadio = async (value: string | number) => {
    if (onChange) {
      onChange(value)
    } else {
      await context.setFieldValue(name, value)
      await context.setFieldTouched(name)
    }
  }

  return (
    <Field name={namePrefix + name} {...inputProps}>
      {(fieldProps: FieldProps<string>) => (
        <RadioGroup<string | number>
          value={fieldProps.field.value}
          error={!isValid ? error : undefined}
          onChange={handleRadio}
          {...inputProps}
        >
          {children}
        </RadioGroup>
      )}
    </Field>
  )
}

export function createFormRadioField<FormValues>() {
  return function TypedField<Name extends ValidateField<DeepNonNullable<DeepRequired<FormValues>>>>(
    props: RadioGroupProps & TypedFieldProps<DeepNonNullable<DeepRequired<FormValues>>, Name>
  ): JSX.Element {
    return <RawRadioGroupField {...props} />
  }
}
